/**
 * §25: графика описывается обычным языком.
 *
 * Между словами и рендером всегда видна структура: пользователь понимает,
 * что именно поймёт система, до того как потратит единицы.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { GraphicsSpec } from '@alterium/shared';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';

/** Вид графики приходит из спецификации: держим ключ, переводим при отрисовке. */
const kindKey = (kind: GraphicsSpec['kind']): string => `enum.graphics.${kind}`;

interface GraphicsEditorProps {
  description: string;
  spec: GraphicsSpec | null;
  busy: boolean;
  onDescribe: (text: string) => void;
}

export function GraphicsEditor({ description, spec, busy, onDescribe }: GraphicsEditorProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(description);
  const changed = text.trim() !== description.trim();

  return (
    <section className="flex flex-col gap-3 rounded-control border border-line bg-surface-secondary p-3.5">
      <p className="text-meta text-content-secondary">{t('graphics.what')}</p>

      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={3}
        placeholder={t('graphics.placeholder')}
        className="resize-none rounded-control border border-line bg-surface px-3 py-2.5 text-body text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
      />

      <Button
        variant="secondary"
        disabled={busy || text.trim().length < 3}
        onClick={() => onDescribe(text.trim())}
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.75} />
        {busy ? t('graphics.parsing') : spec && !changed ? t('graphics.parseAgain') : t('graphics.parse')}
      </Button>

      {spec ? (
        <div className="flex flex-col gap-1.5 rounded-control border border-line bg-surface px-3 py-2.5">
          <p className="text-meta text-content-muted">{t(kindKey(spec.kind))}</p>
          {spec.value ? <p className="text-card-title text-content">{spec.value}</p> : null}
          {spec.title ? <p className="text-body text-content">{spec.title}</p> : null}
          {spec.items?.length ? (
            <ul className="flex flex-col gap-0.5">
              {spec.items.map((item, index) => (
                <li key={`${item}-${index}`} className="text-meta text-content-secondary">
                  · {item}
                </li>
              ))}
            </ul>
          ) : null}
          {spec.description ? (
            <p className="text-meta text-content-secondary">{spec.description}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-meta text-content-muted">{t('graphics.empty')}</p>
      )}
    </section>
  );
}
